import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchLowStockMedicines } from "../services/pharmacyLowStockService";
import {
  canShowPharmacyLowStockAlert,
  readLowStockQuantityThreshold,
} from "../utils/medicineLowStock";
import { getOrganizationId } from "../utils/organizationUtils";
import PharmacyLowStockAlertDialog from "./PharmacyLowStockAlertDialog";

const NavbarPharmacyLowStockButton = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadLowStock = useCallback(async () => {
    if (!canShowPharmacyLowStockAlert(user?.roleName)) return;
    setLoading(true);
    try {
      const list = await fetchLowStockMedicines(getOrganizationId(user));
      setMedicines(list);
    } catch (error) {
      console.warn("[Navbar] Low stock fetch failed:", error);
      setMedicines([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadLowStock();
  }, [loadLowStock]);

  if (!user || !canShowPharmacyLowStockAlert(user.roleName)) {
    return null;
  }

  const count = medicines.length;

  return (
    <>
      <Tooltip
        title={
          count > 0
            ? `${count} medicine(s) low or out of stock — click to view`
            : "No low stock medicines"
        }
      >
        <Badge badgeContent={count > 0 ? count : 0} color="error" max={99}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={loading}
            sx={{ whiteSpace: "nowrap" }}
          >
            Medicine Low Stock
          </Button>
        </Badge>
      </Tooltip>

      <PharmacyLowStockAlertDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        medicines={medicines}
        quantityThreshold={readLowStockQuantityThreshold(getOrganizationId(user))}
        onGoToInventory={() => {
          setDialogOpen(false);
          navigate("/inventory");
        }}
      />
    </>
  );
};

export default NavbarPharmacyLowStockButton;
